import React, { useState } from 'react';
import { XCircle, CheckCircle, AlertCircle, ChevronDown, ChevronRight, FileText, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface OverlapFlag {
  tortCode: string;
  matchType: string;
  confidenceScore: string;
  ruleApplied: string;
  evidenceOnFile: string[];
  evidenceGaps: string[];
}

interface Deficiency {
  deficiencyCode: string;
  message: string;
  severity: string;
  raisedAt: string;
  clearedAt?: string;
}

interface QualificationChecklistProps {
  claimsiqId: string;
  claimantName: string;
  overlapFlags: OverlapFlag[];
  deficiencies?: Deficiency[];
  onClose: () => void;
}

const TORT_COLORS: Record<string, { bg: string; border: string; text: string; light: string }> = {
  RLP: { bg: 'bg-green-600', border: 'border-green-200', text: 'text-green-700', light: 'bg-green-50' },
  PFAS: { bg: 'bg-amber-600', border: 'border-amber-200', text: 'text-amber-700', light: 'bg-amber-50' },
  VGA: { bg: 'bg-blue-600', border: 'border-blue-200', text: 'text-blue-700', light: 'bg-blue-50' },
  SMA: { bg: 'bg-purple-600', border: 'border-purple-200', text: 'text-purple-700', light: 'bg-purple-50' },
};

const TORT_LABELS: Record<string, string> = {
  RLP: 'RealPage Antitrust (MDL-3071)',
  PFAS: 'PFAS/AFFF Exposure',
  VGA: 'Video Game Addiction',
  SMA: 'Social Media Addiction',
};

const CONFIDENCE_STYLES: Record<string, { label: string; cls: string }> = {
  confirmed: { label: 'Confirmed', cls: 'bg-green-100 text-green-700' },
  probable: { label: 'Probable', cls: 'bg-amber-100 text-amber-700' },
  unverified: { label: 'Unverified', cls: 'bg-gray-100 text-gray-600' },
};

const RULE_CRITERIA: Record<string, string> = {
  'RULE-001': 'Address matches defendant property via Smarty Streets + DynamoDB lookup',
  'RULE-007': 'Address proximity to known PFAS contamination site',
  'TRIG-002': 'Gaming hours per day meets threshold (2+ hrs/day)',
  'TRIG-003': 'Social media platform usage evidence detected',
  'RULE-002': 'Residency dates overlap class period (2018-01-01+)',
  'RULE-003': 'Military service branch confirmed at affected installation',
  'RULE-004': 'Occupational exposure pathway established',
  'RULE-005': 'Minor at time of exposure (under 18)',
  'RULE-006': 'Qualifying diagnosis (ICD-10) documented',
};

export const QualificationChecklist: React.FC<QualificationChecklistProps> = ({
  claimsiqId,
  claimantName,
  overlapFlags,
  deficiencies,
  onClose,
}) => {
  const [expandedTort, setExpandedTort] = useState<string | null>(overlapFlags[0]?.tortCode || null);

  const toggleTort = (code: string) => {
    setExpandedTort(expandedTort === code ? null : code);
  };

  const totalPassed = overlapFlags.reduce((acc, f) => acc + (f.evidenceGaps.length === 0 ? 1 : 0), 0);
  const totalFlags = overlapFlags.length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Qualification Checklist</h2>
              <p className="text-sm text-gray-500 mt-0.5">{claimsiqId} &middot; {claimantName}</p>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <XCircle className="h-5 w-5 text-gray-400" />
            </button>
          </div>
          {/* Summary bar */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Torts Evaluated:</span>
              <div className="flex gap-1">
                {overlapFlags.map((f) => {
                  const colors = TORT_COLORS[f.tortCode] || TORT_COLORS.RLP;
                  return (
                    <span key={f.tortCode} className={`${colors.bg} text-white text-xs font-bold px-2 py-0.5 rounded`}>
                      {f.tortCode}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              <span className="font-semibold text-gray-800">{totalPassed}</span> / {totalFlags} fully qualified
            </div>
          </div>
        </div>

        {/* Per-tort checklists */}
        <div className="p-6 space-y-4">
          {overlapFlags.map((flag) => {
            const colors = TORT_COLORS[flag.tortCode] || TORT_COLORS.RLP;
            const conf = CONFIDENCE_STYLES[flag.confidenceScore] || CONFIDENCE_STYLES.unverified;
            const isExpanded = expandedTort === flag.tortCode;
            const allEvidenceMet = flag.evidenceGaps.length === 0;
            const criterionDesc = RULE_CRITERIA[flag.ruleApplied] || flag.ruleApplied;

            return (
              <div key={flag.tortCode} className={`border ${colors.border} rounded-lg overflow-hidden`}>
                {/* Tort header row */}
                <button
                  onClick={() => toggleTort(flag.tortCode)}
                  className={`w-full flex items-center justify-between p-4 ${colors.light} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                    <span className={`${colors.bg} text-white text-xs font-bold px-2 py-0.5 rounded`}>{flag.tortCode}</span>
                    <span className="text-sm font-semibold text-gray-800">{TORT_LABELS[flag.tortCode] || flag.tortCode}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${conf.cls}`}>{conf.label}</span>
                    {allEvidenceMet ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                    )}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="p-4 border-t border-gray-100 space-y-4">
                    {/* Match summary */}
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Match Type</p>
                        <p className="text-sm font-semibold text-gray-800 capitalize">{flag.matchType}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Confidence</p>
                        <p className={`text-sm font-semibold capitalize ${conf.cls} inline-block px-2 py-0.5 rounded`}>{flag.confidenceScore}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Rule Applied</p>
                        <p className="text-sm font-mono font-semibold text-gray-800">{flag.ruleApplied}</p>
                      </div>
                    </div>

                    {/* Criterion evaluated */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Qualifying Criterion</h4>
                      <div className={`flex items-start gap-2 p-3 rounded-lg ${allEvidenceMet ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        {allEvidenceMet ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        )}
                        <p className="text-sm text-gray-800">{criterionDesc}</p>
                      </div>
                    </div>

                    {/* Evidence on file */}
                    {flag.evidenceOnFile.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Evidence On File</h4>
                        <div className="space-y-1.5">
                          {flag.evidenceOnFile.map((ev, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                              <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{ev}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evidence gaps */}
                    {flag.evidenceGaps.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Evidence Gaps</h4>
                        <div className="space-y-1.5">
                          {flag.evidenceGaps.map((gap, idx) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
                              <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                              <span className="text-sm text-red-700 font-medium">{gap}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Deficiency summary if any */}
          {deficiencies && deficiencies.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4 mt-2">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Active Deficiency Notices ({deficiencies.length})</h4>
              <div className="space-y-2">
                {deficiencies.map((def, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Badge className={`text-xs ${def.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : def.severity === 'WARNING' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {def.severity}
                    </Badge>
                    <span className="text-xs font-mono text-gray-600">{def.deficiencyCode}</span>
                    <span className="text-xs text-gray-500 truncate">{def.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 rounded-b-xl">
          <Button onClick={onClose} variant="outline" className="w-full">Close</Button>
        </div>
      </div>
    </div>
  );
};

export default QualificationChecklist;